import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { FileText, Download } from 'lucide-react';

interface Job {
  jobid: string;
  "file name": string;
  requestor: string;
  "S3-pdfname": string;
  "download-url": string;
}

const JobHistory: React.FC = () => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    if (user?.email) {
      fetchJobs();
    }
  }, [user]);

  const fetchJobs = async () => {
    if (!user?.email) return;

    try {
      setLoading(true);
      console.log('Fetching jobs for email:', user.email);
      
      // Create URL with query parameters
      const url = new URL('https://automation.simdigi.xyz/webhook/50fe3c8a-42e4-453d-97be-595aa7c750a2');
      url.searchParams.append('email', user.email);
      
      console.log('Fetching jobs with URL:', url.toString());
      
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch jobs: ${response.status} ${response.statusText}`);
      }

      // Get the response as text first for debugging
      const responseText = await response.text();
      console.log('Response length:', responseText.length);
      
      // Check if response is empty
      if (!responseText.trim()) {
        console.error('Empty response received');
        setJobs([]);
        setError('No data received from server');
        setLoading(false);
        return;
      }
      
      // Try to parse the JSON
      let data;
      try {
        data = JSON.parse(responseText);
        console.log('Jobs count in response:', Array.isArray(data) ? data.length : 'Not an array');
      } catch (parseError) {
        console.error('Error parsing JSON:', parseError);
        throw new Error('Invalid JSON response from server');
      }
      
      // Process the data depending on its format
      let processedJobs = [];
      
      if (Array.isArray(data)) {
        console.log('Data is an array with length:', data.length);
        processedJobs = data;
      } else if (data && typeof data === 'object') {
        console.log('Data is an object, checking for array properties');
        // Try to find an array property
        const arrayProp = Object.values(data).find(val => Array.isArray(val));
        if (arrayProp) {
          console.log('Found array property with length:', arrayProp.length);
          processedJobs = arrayProp;
        } else {
          // Treat the object as a single job
          console.log('Treating object as a single job');
          processedJobs = [data];
        }
      } else {
        console.log('Data is neither array nor object:', typeof data);
        processedJobs = [];
      }
      
      console.log('Processed jobs count:', processedJobs.length);
      
      // Clean up the jobs data
      const cleanedJobs = processedJobs
        .filter(job => job && typeof job === 'object')
        .map(job => {
          const cleanJob = { ...job };
          
          // Ensure file name exists
          if (!('file name' in cleanJob) || !cleanJob['file name']) {
            cleanJob['file name'] = 'Unnamed File';
          }
          
          // Clean up jobid if it contains template variables
          if (cleanJob.jobid && typeof cleanJob.jobid === 'string' && cleanJob.jobid.includes('{{')) {
            cleanJob.jobid = '';
          }
          
          // Clean up empty requestor fields
          if (!cleanJob.requestor || cleanJob.requestor === ' ') {
            cleanJob.requestor = 'Unknown';
          }
          
          // Ensure S3-pdfname exists
          if (!('S3-pdfname' in cleanJob)) {
            cleanJob['S3-pdfname'] = '';
          }
          
          // Ensure download-url exists
          if (!('download-url' in cleanJob)) {
            cleanJob['download-url'] = '';
          }
          
          return cleanJob;
        });
      
      console.log('Cleaned jobs count:', cleanedJobs.length);
      
      // Convert all cleaned jobs to the proper Job type
      const allJobs: Job[] = cleanedJobs.map(job => job as Job);
      
      console.log('Total jobs count:', allJobs.length);
      
      // Log the first few jobs for debugging
      allJobs.slice(0, 3).forEach((job, index) => {
        console.log(`Job ${index}:`, job);
      });
      
      // Sort jobs with newest at the top
      // Since we don't have explicit timestamps, we'll reverse the array
      // assuming that newer jobs are added to the end of the array
      allJobs.reverse();
      
      console.log('Jobs after sorting (newest first):', allJobs.length);
      
      setJobs(allJobs);
      setError(null);
    } catch (err) {
      console.error('Error fetching jobs:', err);
      setError('Failed to load jobs. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = (job: Job) => {
    if (!job["download-url"] && !job["S3-pdfname"]) {
      alert('PDF is not available for this job yet.');
      return;
    }

    // Use download-url if available, otherwise construct a URL from S3-pdfname
    const downloadUrl = job["download-url"] || 
      (job["S3-pdfname"] ? `https://storage.simdigi.xyz/transcripts/${job["S3-pdfname"]}.pdf` : null);
    
    if (downloadUrl) {
      // Open the download URL in a new tab
      window.open(downloadUrl, '_blank');
    } else {
      alert('PDF is not available for this job yet.');
    }
  };

  if (loading) {
    return <div className="text-center py-4">Loading jobs...</div>;
  }

  if (error) {
    return (
      <div className="text-center py-4">
        <p className="text-red-500">{error}</p>
        <button 
          onClick={fetchJobs}
          className="mt-2 px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!jobs || jobs.length === 0) {
    return <div className="text-center py-4">No jobs found.</div>;
  }

  return (
    <div className="overflow-x-auto">
      <div className="mb-4 flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">Your Transcription Jobs</h3>
        <button 
          onClick={fetchJobs}
          className="px-3 py-1 bg-teal-100 text-teal-800 rounded-md text-sm flex items-center hover:bg-teal-200 transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </button>
      </div>
      
      <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg overflow-hidden">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">File Name</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Requestor</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {jobs.map((job, index) => {
            // Determine if PDF is available
            const pdfAvailable = job["download-url"] || job["S3-pdfname"];
            
            return (
              <tr key={job.jobid || `job-${index}`} className="hover:bg-gray-50">
                <td className="px-6 py-4 text-sm font-medium text-gray-900">
                  <div className="flex items-center">
                    <FileText className="h-4 w-4 text-teal-500 mr-2 flex-shrink-0" />
                    <span className="truncate max-w-xs">{job["file name"]}</span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    pdfAvailable 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {pdfAvailable ? 'Completed' : 'Processing'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {job.requestor || 'Unknown'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <button
                    onClick={() => handleDownloadPDF(job)}
                    disabled={!pdfAvailable}
                    className={`inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md ${
                      !pdfAvailable
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-teal-600 text-white hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500'
                    }`}
                  >
                    <Download className="w-4 h-4 mr-1" />
                    {pdfAvailable ? 'Download PDF' : 'Pending'}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      
      {jobs.length === 0 && !loading && !error && (
        <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200 mt-4">
          <FileText className="h-12 w-12 text-gray-400 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">No transcription jobs found</h3>
          <p className="text-gray-500">Upload an audio file to get started with transcription.</p>
        </div>
      )}
    </div>
  );
};

export default JobHistory;
